import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, message, InputNumber, DatePicker, Row, Col, Switch } from "antd";
import { createCommissionConfig, updateCommissionConfig } from "../../../services/commission/commissionServices";
import moment from "moment";

const { Option } = Select;

const CommissionConfigModal = ({ visible, onCancel, onSuccess, initialValues, commissionTypes }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const isEdit = !!initialValues;

  useEffect(() => {
    if (visible) {
      if (initialValues) {
        // Convert ISO strings to moment objects for DatePicker
        const formData = {
          ...initialValues,
          effectiveFrom: initialValues.effectiveFrom ? moment(initialValues.effectiveFrom) : null,
          effectiveTo: initialValues.effectiveTo ? moment(initialValues.effectiveTo) : null,
        };
        form.setFieldsValue(formData);
      } else {
        form.resetFields();
      }
    }
  }, [visible, initialValues, form]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        commissionTypeId: Number(values.commissionTypeId),
        commissionName: values.commissionName || "",
        minAmount: Number(values.minAmount || 0),
        maxAmount: Number(values.maxAmount || 0),
        commissionValue: Number(values.commissionValue || 0),
        commissionUnit: values.commissionUnit,
        customerCategory: values.customerCategory || "",
        isActive: !!values.isActive,
        effectiveFrom: values.effectiveFrom?.toISOString(),
        effectiveTo: values.effectiveTo?.toISOString(),
        appliesToChannel: values.appliesToChannel || "",
        maxCommissionCap: Number(values.maxCommissionCap || 0),
        createdBy: values.createdBy || localStorage.getItem('userName') || 'Admin',
      };

      let res;
      if (isEdit) {
        res = await updateCommissionConfig(initialValues.id, payload);
      } else {
        res = await createCommissionConfig(payload);
      }

      if (res?.success) {
        message.success(`Configuration ${isEdit ? "updated" : "created"} successfully`);
        onSuccess();
      } else {
        message.error(res?.message || `Failed to ${isEdit ? "update" : "create"} configuration`);
      }
    } catch (error) {
      console.error(error);
      message.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={isEdit ? "Edit Configuration" : "Add Commission Configuration"}
      visible={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={700}
      destroyOnClose
      okText={isEdit ? "Update" : "Save Configuration"}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ 
          isActive: true, 
          commissionUnit: "FIXED_AMOUNT",
          minAmount: 0,
          maxAmount: 0,
          commissionValue: 0,
          maxCommissionCap: 0
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="commissionTypeId"
              label="Commission Type"
              rules={[{ required: true, message: "Please select a type" }]}
            >
              <Select placeholder="Select Type">
                {commissionTypes.map(t => (
                  <Option key={t.id} value={t.id}>{t.commissionName}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="commissionName"
              label="Configuration Profile Name"
              rules={[{ required: true, message: "Please enter a profile name" }]}
            >
              <Input placeholder="e.g. Standard Tier 1" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="minAmount"
              label="Minimum Transaction Amount (₦)"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber style={{ width: '100%' }} min={0} placeholder="0" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="maxAmount"
              label="Maximum Transaction Amount (₦)"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber style={{ width: '100%' }} min={0} placeholder="1,000,000" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="commissionUnit"
              label="Commission Unit"
              rules={[{ required: true, message: "Required" }]}
            >
              <Select>
                <Option value="FIXED_AMOUNT">FIXED AMOUNT (₦)</Option>
                <Option value="PERCENTAGE">PERCENTAGE (%)</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="commissionValue"
              label="Commission Value"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} placeholder="10.00" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="customerCategory"
              label="Customer Category"
              rules={[{ required: true, message: "Please specify category" }]}
            >
              <Select placeholder="Select category">
                <Option value="A">CATEGORY A</Option>
                <Option value="B">CATEGORY B</Option>
                <Option value="C">CATEGORY C</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="appliesToChannel"
              label="Applies to Channel(s)"
              rules={[{ required: true, message: "Please specify channel(s)" }]}
            >
              <Select placeholder="Select channel" defaultValue="ALL">
                <Option value="ALL">ALL (Default)</Option>
                <Option value="WAYABANK">WAYABANK (Web/Mobile App)</Option>
                <Option value="CARD">CARD Transactions</Option>
                <Option value="POS_TERMINAL">POS TERMINAL</Option>
                <Option value="POS_COMMISSION">POS COMMISSION Payouts</Option>
                <Option value="WEB_TERMINAL">WEB TERMINAL</Option>
                <Option value="WEB_COMMISSION">WEB COMMISSION Payouts</Option>
                <Option value="POS_AGGREGATOR_COMMISSION">POS Aggregator Commission</Option>
                <Option value="WEB_AGGREGATOR_COMMISSION">Web Aggregator Commission</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="effectiveFrom"
              label="Effective From"
              rules={[{ required: true, message: "Date required" }]}
            >
              <DatePicker style={{ width: '100%' }} showTime format="YYYY-MM-DD HH:mm:ss" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="effectiveTo"
              label="Effective To"
              rules={[{ required: true, message: "Date required" }]}
            >
              <DatePicker style={{ width: '100%' }} showTime format="YYYY-MM-DD HH:mm:ss" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="maxCommissionCap"
              label="Max Commission Cap (₦)"
            >
              <InputNumber style={{ width: '100%' }} min={0} placeholder="0 (No limit)" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="isActive"
              label="Status"
              valuePropName="checked"
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="createdBy" hidden>
           <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CommissionConfigModal;
